import {
    Stack, Card, Container, Typography,
    Box, Tabs, Tab, CardContent, Fab,
    Button, Slide, Input, CircularProgress, Backdrop,
    AppBar, Toolbar, Grid, Divider
} from '@mui/material';
import React, { useEffect, useState, useContext } from 'react';
import { Add, HolidayVillage, Search, Close, ArrowBackIos } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';
import * as api from '../../api';
import * as dict from '../../dictionary';
import { ProfileContext } from '../../contexts/ProfileContext';
import { BlankPage, ModalError } from '../../ui-component';
import { StatusTag } from './StatusTag';
import moment from 'moment';
import { ChipRoomInfo } from '../../ui-component/ChipRoomInfo';
import { checkBase64 } from '../../utils/checkBase64';
import { getRoomTypeName } from './GetRoomTypeName';

export const ServiceRequestList = (props) => {
    const domain = window.location.origin;

    if (props?.version === "fastcare") {
        document.title = "Fast Care";
        let link = document.querySelector("link[rel~='icon']");
        if (!link) {
            link = document.createElement('link');
            link.rel = 'icon';
            document.getElementsByTagName('head')[0].appendChild(link);
        }
        link.href = domain + "/fast-favicon.ico";
    }

    const [tabSelected, setTabSelected] = useState(sessionStorage.getItem("tabSelected") || "waiting");
    const [serviceRequestList, setServiceRequestList] = useState([]);
    const [triggerSearch, setTriggerSearch] = useState(false);
    const [loadingList, setLoadingList] = useState(false);
    const [profile, setProfile] = useState(null);
    const [houseInfo, setHouseInfo] = useState(null);
    const [modalError, setModalError] = useState(false);
    const [error, setError] = useState(null);
    const [keyword, setKeyword] = useState(null);
    const [clientId, setClientId] = useState(null);
    const [lang, setLang] = useState(sessionStorage.getItem("fast-lang") || "th");

    const context = useContext(ProfileContext);
    const location = useLocation();
    const navigate = useNavigate();

    const params = new URLSearchParams(location.search);
    const unitText = params.get("unitText");
    const unitNo = params.get("unitNo");
    const [seqWorkTypeList, setSeqWorkTypeList] = useState([]);

    const loadDataList = async () => {
        setLoadingList(true);
        try {
            let res;

            // API call
            if (props?.version === "fastcare") {
                let client_id, client_url, project_id;
                if (checkBase64(unitText)) {
                    const projectInfo = window.atob(unitText).split("|");
                    client_id = parseInt(projectInfo[0]);
                    client_url = projectInfo[1];
                    project_id = parseInt(projectInfo[2]);
                    setClientId(client_id);
                }

                res = await api.getWorkOrderDocumentNoAuth({
                    client_id,
                    client_url,
                    project_id,
                    version: "fastcare"
                });
            } else {
                if (!profile || !profile.company_code || !houseInfo?.project_id) {
                    console.warn("Missing profile or houseInfo data");
                    setLoadingList(false);
                    return;
                }

                res = await api.getWorkOrderDocumentNoAuth({
                    company_code: profile.company_code,
                    project_ref_id: houseInfo.project_id,
                    client_url: profile.url_api_fast_inspect.split("/api/v1")[0],
                    module_type: "PROJECT",
                    wo_status: "",
                    is_not_filtered_user: "Y",
                    version: 2
                });
            }

            if (res?.status === 200) {
                const result = res.data?.result || {};
                let list = result?.wo || [];
                setSeqWorkTypeList(result?.seq_work_type || []);

                // Filter ทั้ง FastCare และ Line OA
                if (tabSelected === "waiting") {
                    list = list.filter(item => item.wo_status !== "P" && item.wo_status !== "R");
                } else if (tabSelected === "history") {
                    list = list.filter(item => item.wo_status === "P" || item.wo_status === "R");
                }

                list.sort((a, b) => new Date(b.wo_opened_date) - new Date(a.wo_opened_date));

                setServiceRequestList(list);
            } else {
                setError(res);
                setModalError(true);
            }
        } catch (err) {
            setError(err);
            setModalError(true);
        }
        setLoadingList(false);
    };

    const loadDataHistory = async () => {
        if (props?.version === "fastcare") {
            // FastCare ไม่ต้องเช็ค houseInfo
            await loadDataList();
            return;
        }

        // Line OA ต้องเช็ค houseInfo
        if (!houseInfo) return;
        await loadDataList();
    };

    const loadDetail = async (reference_code) => {
        if (props?.version === "fastcare") {
            const res = await api.getDefectDetailFastCare({ client_id: clientId, reference_code });
            return res?.data;
        }

        const res = await api.getDefectDetail({
            line_id: profile?.line_id,
            reference_code,
            project_id: houseInfo?.project_id,
            house_number: houseInfo?.house_number,
            product_type: houseInfo?.product_type,
            version: props?.version
        });
        return res?.data;
    };

    const handleClickDetail = (referenceCode) => {
        if (props?.version === "fastcare") {
            navigate(`/service-request/detail${location.search}&reference_code=${referenceCode}`, { state: { reference_code : referenceCode} });
        } else {
            navigate(`/line-oa/service-request/detail${location.search}&reference_code=${referenceCode}`, { state: { reference_code : referenceCode} });
        }
        };

    const handleSelectedTab = (tab) => {
        window.scrollTo(0, 0);
        setServiceRequestList([]);
        if (tab === "history") return loadDataHistory();
        return handleChangeHouseInfo();
    };

    const handleChangeHouseInfo = () => {
        if (!houseInfo && props?.version !== "fastcare") return;
        return loadDataList();
    };

    useEffect(() => {
        handleSelectedTab(sessionStorage.getItem("tabSelected") || tabSelected);
    }, [tabSelected, houseInfo]);

    useEffect(() => {
        if (context.state?.profile) setProfile(context.state?.profile?.customer[0]);
        if (context.state?.houseInfo) setHouseInfo(context.state?.houseInfo);
    }, [context]);

    useEffect(() => {
        if (keyword !== null) {
            const timeoutId = setTimeout(() => {
                if (houseInfo || props?.version === "fastcare") handleSelectedTab(tabSelected);
            }, 1000);
            return () => clearTimeout(timeoutId);
        }
    }, [keyword]);

    useEffect(() => {
        const handleLangChange = () => setLang(sessionStorage.getItem("fast-lang") || "th");
        window.addEventListener("storage", handleLangChange);
        return () => window.removeEventListener("storage", handleLangChange);
    }, []);

    return (
        <>
            <AppBar sx={{ position: 'fixed', boxShadow: 'none', height: 60 }}>
                <Toolbar>
                    <Grid container item xs={1}>
                        <ArrowBackIos
                            onClick={() => {
                                if (props?.version === "fastcare") {
                                    navigate(`/fastcare${location.search.split("&")[0]}`);
                                } else {
                                    // กรณี Line OA: ให้ย้อนกลับไปหน้าก่อนหน้า (dynamic)
                                   navigate(-1);
                                }
                            }}  
                        />
                    </Grid>
                    <Grid container item xs={10} justifyContent="center">
                        <Typography>{dict.SERVICE_REQUEST_LIST_TXT[profile?.language || lang]}</Typography>
                    </Grid>
                </Toolbar>
            </AppBar>

            <Box sx={{ minHeight: "100dvh", mt: "60px" }}>
                <Box sx={{ minWidth: "100%", position: "fixed", zIndex: 10 }}>
                    <Tabs
                        value={tabSelected}
                        onChange={(e, val) => { sessionStorage.setItem('tabSelected', val); setTabSelected(val); }}
                        textColor="secondary"
                        indicatorColor="secondary"
                        centered
                        variant="fullWidth"
                        sx={{ backgroundColor: "white" }}
                    >
                        <Tab value="waiting" label={dict.Waiting_TXT[profile?.language || lang]} />
                        <Tab value="history" label={dict.HISTORY_TXT[profile?.language || lang]} />
                    </Tabs>
                </Box>

                <Backdrop sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }} open={loadingList}>
                    <Stack alignItems="center">
                        <CircularProgress color="inherit" />
                    </Stack>
                </Backdrop>

                <Container sx={{ minHeight: "100dvh", paddingBottom: 4 }}>
                    <Box sx={{ width: "100%", overflow: "hidden" }}>
                        <Stack direction="row" mt={8}>
                            <Slide direction="right" in={!triggerSearch} mountOnEnter unmountOnExit>
                                <Stack sx={{ width: "100%" }} direction="row" alignItems="center" justifyContent="space-between">
                                    <ChipRoomInfo
                                        houseInfo={props?.version === "fastcare" ? { unit_number: unitNo } : houseInfo}
                                        icon={<HolidayVillage />}
                                        lang={profile?.language || lang}
                                        version={props?.version}
                                    />
                                    <Button size="small">
                                        <Search sx={{ fontSize: 35 }} onClick={() => setTriggerSearch(true)} />
                                    </Button>
                                </Stack>
                            </Slide>
                        </Stack>

                        <Slide direction="left" timeout={{ enter: 500, exit: 500 }} in={triggerSearch} mountOnEnter unmountOnExit>
                            <Box sx={{ width: "100%" }}>
                                <Input placeholder={dict.SearchDefect_TXT[profile?.language || lang]} value={keyword} onChange={(e) => setKeyword(e.target.value)} />
                                <Button size="small">
                                    <Close onClick={() => { setTriggerSearch(false); setKeyword(""); }} />
                                </Button>
                            </Box>
                        </Slide>
                    </Box>

                    <Box mt={2}>
                        <Stack spacing={1}>
                            {serviceRequestList?.length > 0 ? serviceRequestList.map((element, i) => (
                                <Card key={i} sx={{ minWidth: 275, borderRadius: "10px", boxShadow: "0px 0px 7px 0px #00000040" }}>
                                    <CardContent>
                                        <Stack direction="column" spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                <Typography variant="caption">{dict.REFERENCE_CODE_TXT[profile?.language || lang]}</Typography>
                                                <Typography fontSize={12} fontWeight={700}>
                                                    {element?.reference_code || '-'}
                                                </Typography>
                                            </Stack>

                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                <Typography variant="caption">
                                                    {dict.SEQ_WORK_TYPE_NAME_TXT[profile?.language || lang]}
                                                </Typography>

                                                <Stack direction="column" alignItems="flex-end" spacing={0.3}>
                                                    {(() => {
                                                        const matchedTypes = element?.wo_item
                                                            ?.map(item => seqWorkTypeList.find(
                                                                type => type.seq_work_type_id === item.seq_work_type_id
                                                            ))
                                                            ?.filter(Boolean);

                                                        if (!matchedTypes || matchedTypes.length === 0) {
                                                            return <Typography fontSize={12} fontWeight={700}>-</Typography>;
                                                        }

                                                        return matchedTypes.map((type, idx) => (
                                                            <Typography key={idx} fontSize={12} fontWeight={700}>
                                                                {(profile?.language === "th" || lang === "th")
                                                                    ? (type.seq_work_type_name_th || "-")
                                                                    : (type.seq_work_type_name || "-")}
                                                            </Typography>
                                                        ));
                                                    })()}
                                                </Stack>
                                            </Stack>

                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                <Typography variant="caption">{dict.CONTRACT_UNIT_TYPE_TXT[profile?.language || lang]}</Typography>
                                                <Typography fontSize={12} fontWeight={700}>
                                                    {getRoomTypeName(element?.contract_unit_type, profile?.language || lang)}
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        <Divider sx={{ my: 1.5 }} />

                                        <Stack direction="column" spacing={1}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                <Typography variant="caption">{dict.SERVICE_REQUEST_LIST_Status_TXT[profile?.language || lang]}</Typography>
                                                <StatusTag code={element?.wo_status} lang={profile?.language || lang} version={props?.version} />
                                            </Stack>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                                                <Typography variant="caption">{dict.REQUEST_DATE_TXT[profile?.language || lang]}</Typography>
                                                <Typography fontSize={12} fontWeight={700}>
                                                    {moment.utc(element.wo_opened_date).locale(profile?.language || sessionStorage.getItem("fast-lang") || "th").format('DD/MM/YYYY') }
                                                </Typography>
                                            </Stack>
                                        </Stack>

                                        <Stack direction="row" justifyContent="center" mt={2}>
                                            <Button
                                                size="large"
                                                onClick={() => handleClickDetail(element.reference_code)}
                                                sx={{
                                                    borderRadius: "10px",
                                                    boxShadow: "0px 0px 7px 0px #00000040",
                                                    width: "215.44px"
                                                }}
                                            >
                                                {dict.SERVICE_REQUEST_LIST_More_TXT[profile?.language || lang]}
                                            </Button>
                                        </Stack>
                                    </CardContent>
                                </Card>
                            )) : <BlankPage lang={profile?.language || lang} />}
                        </Stack>
                    </Box>

                    {(tabSelected === "waiting" && props?.version !== "fastcare") &&
                        <Box style={{ margin: 0, top: 'auto', right: 20, bottom: 20, left: 'auto', position: 'fixed' }}>
                            <Fab variant="primary" size='large' color="secondary"
                                onClick={() => navigate('/line-oa/service-request', { state: { version: props?.version } })}>
                                <Add />
                            </Fab>
                        </Box>
                    }
                </Container>
            </Box>

            <ModalError onClose={() => setModalError(false)} open={modalError} error={error} />
        </>
    );
};
